<template>
  <!--Incidents Modal -->
  <b-modal
    id="common-incident-modal-tree"
    ref="bModalA"
    size="xxl"
    modal-class="incidentsModal"
    static
    centered
    :title="$tc('global.statusOptions.incidence',2)"
    hide-footer
  >
    <b-row
      style="height:100%"
      class="pl-3"
    >
      <hierarchical-patient-tree
        v-if="!isGeneral"
        ref="patientTree"
        @itemSelected="loadIncidents($event)"
      />
      <b-col
        v-if="entityInformation"
        class="content"
      >
        <b-row>
          <b-col class="attach-header bottom-bordered py-2 d-flex justify-content-between">
            <div class="d-flex align-items-center">
              <div
                class="text-center"
                style="width: 25px"
              >
                <i
                  aria-hidden="true"
                  class="mr-2 primary-dark-color"
                  :class="entityInformation.icon ? entityInformation.icon : 'far fa-hospital'"
                />
              </div>
              <span
                qa="entity-label"
                class="ap-title ap-title__section mx-2"
              >{{ entityInformation.label ? entityInformation.label : laboratorySelectedName }}</span>
              <vtBubble
                v-if="entityInformation.incidentsCount"
                qa="incident-bubble"
                :number="entityInformation.incidentsCount"
                :type="true"
                :tooltip="` ${$tc('global.incident',entityInformation.incidentsCount)}`"
              />
            </div>
            <div class="d-flex align-items-center">
              <grid-display-options
                v-if="gridApi"
                :ui-component="gridUiComponent"
                @changedPageSize="changedPageSize($event)"
                @changedMode="changedMode($event)"
              />

              <vt-report-export-buttons
                v-if="$_canPrintIncident()"
                source="incidents-grid"
                :export-report="exportReport"
              />

              <b-button
                v-if="$_canCreateIncident() && canEntityCreateIncidents"
                v-tooltip="$t('component.incidentsModal.addIncidence')"
                qa="register-incident-button"
                class="btn__icon btn__icon--primary pt-1 pr-4"
                @click="openIncidentFormModal"
              >
                <i
                  class="far fa-exclamation-triangle"
                  aria-hidden="true"
                />
              </b-button>
            </div>
          </b-col>
        </b-row>
        <b-row
          v-if="columnDefs"
          class="attach-content"
        >
          <div
            class="grid mt-1 w-100"
            qa="incidents-grid"
          >
            <vt-grid
              class="incidents-grid"
              :grid-options="gridOptions"
              :column-defs="columnDefs"
              :row-data="rowData"
            />
          </div>
          <div
            class="sidepanel"
            :class="{'animation': animate}"
          >
            <div class="header px-4 py-2 d-flex align-items-center justify-content-between">
              <span class="title">{{ $tc('global.file', 2) }}</span>
              <div>
                <b-button
                  class="btn__icon btn__icon--primary"
                  @click="animate=!animate"
                >
                  <i
                    class="far fa-times"
                    aria-hidden="true"
                  />
                </b-button>
              </div>
            </div>
            <div
              v-if="attachments.length"
              class="content"
            >
              <div class="element">
                <div
                  v-for="(attachment, index) in attachments"
                  :key="attachment.attachmentId"
                  class="file d-flex align-items-center justify-content-between px-3"
                >
                  <span>
                    <b-button
                      class="btn secondary-button btn-main"
                      @click="downloadAttachment(attachment.attachmentId, index)"
                    >
                      {{ attachment.filename }}
                    </b-button>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </b-row>
      </b-col>
    </b-row>
    <incident-form-modal
      v-if="entityInformation"
      id="incidentFormModal"
      ref="incidentFormModal"
      :core-entity="entityInformation.type"
      @incident-added="onIncidentAdded(entityInformation)"
      @incident-closed="onIncidentClosed(entityInformation)"
    />
    <print-component ref="printComponent" />
  </b-modal>
</template>

<script>
import VtBubble from '@/components/common/VtBubble'
import VtGrid from '@/components/VtGrid/VtGrid'
import { commonIncidentModalGridColumnDefs } from '@/components/incidentsModal/grid/common-incident-modal-grid.column-defs'
import IncidentServices, { COREENTITY_VALUES } from '@/api/resources/incident/incidentServices'
import HierarchicalPatientTree from '@/components/incidentsModal/hierarchicalPatientTree'
import IncidentFormModal from '@/components/incidentsModal/IncidentFormModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import incidentAttachmentService from '@/api/resources/attachment/incidentAttachmentService'
import { saveAs } from 'file-saver'
import { StoreUtils } from '@/shared/util/store-utils'
import GridDisplayOptions from '@/components/GridDisplayOptions'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import { GridExportMixin } from '@/mixins/grid-export.mixin'
import IncidentSeverityServices from '@/api/resources/incident/incidentSeverityServices'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'

const ROW_HEIGHT_NORMAL = 40
const ROW_HEIGHT_COMPACT = 25

export default {
    name: 'CommonIncidentModalTree',
    components: {
        GridDisplayOptions,
        VtBubble,
        VtGrid,
        HierarchicalPatientTree,
        IncidentFormModal
    },
    mixins: [
        IncidentOperationGuardMixin,
        GridExportMixin
    ],
    props: {
        initMode: { type: Object, default: () => ({ value: 'compact', icon: 'fal fa-list-ul' }) },
        id: { type: String },
        content: { type: String },
        okTitle: { type: String, default: 'global.close' },
        defaultOption: { type: Object },
        isRequest: { type: Boolean, default: false },
        isGeneral: { type: Boolean, default: false }
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.incidents.incidentgrid',
            laboratorySelectedName: null,
            indentIdAttachOpen: null,
            attachments: [],
            rowData: [],
            entityInformation: null,
            selectView: false,
            animate: false,
            showAttach: false,
            showArrow: false,
            btnPressed: false,
            hideCases: false,
            hideSamples: false,
            hidePortions: false,
            hideSubportions: false,
            selected: null,
            options: [
                { value: null, text: '' },
                { value: 'a', text: 'Opción 1' },
                { value: 'b', text: 'Opción 2' },
                { value: 'b', text: 'Opción 3' }
            ],
            mode: this.initMode,
            optionsmulti: [
                { value: 'compact', icon: 'fal fa-list-ul' },
                { value: 'normal', icon: 'fal fa-th-list' },
                { value: 'card', icon: 'fal fa-columns' }
            ],
            columnDefs: null,
            columnApi: null,
            gridApi: null,
            selectedRows: []
        }
    },
    computed: {
        entityInformationMetadataOperationGuards() {
            return this.entityInformation.metadata?.operationGuards
        },
        canEntityCreateIncidents() {
            let canCreateIncidents
            if (this.entityInformation.type === COREENTITY_VALUES.REQUEST.code ||
                this.entityInformation.type === COREENTITY_VALUES.PATIENT.code ||
                this.entityInformation.type === COREENTITY_VALUES.GENERAL.code) {
                canCreateIncidents = true
            } else {
                canCreateIncidents = this.entityInformationMetadataOperationGuards?.canCreateIncidents
            }
            return canCreateIncidents
        },
        msgSelectedRows() {
            if (this.selectedRows.length) {
                const choice = this.selectedRows.length > 1 ? 2 : 1
                return `${this.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
            }
            return ''
        },
        rowHeight() {
            if (this.mode.value === 'compact') {
                return ROW_HEIGHT_COMPACT
            }
            return ROW_HEIGHT_NORMAL
        },
        gridOptions() {
            return {
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                rowModelType: AgGridRowModelType.CLIENT_SIDE,
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                getRowHeight: params => {
                    if (params.data) {
                        if (params.data.notifiedPersonnelList.length) {
                            return this.rowHeight * params.data.notifiedPersonnelList.length
                        } else {
                            return this.rowHeight
                        }
                    }
                },
                onRowDoubleClicked: (row) => this.openIncidentFormModal({ node: row }),
                autoGroupColumnDef: {
                    headerName: 'Consoles', // custom header name for group
                    pinned: 'left', // force pinned left. Does not work in columnDef
                    cellRendererParams: {
                        suppressCount: true// remove number in Group Column
                    }
                }
            }
        },
        labId() {
            return this.$store.getters.labId
        }
    },
    methods: {
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.gridApi.resetRowHeights()
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        async downloadAttachment(attId, index) {
            const blob =
                await this.$_toCallSecure(
                    incidentAttachmentService.getFile(this.labId, this.indentIdAttachOpen, attId))
            if (blob) {
                saveAs(blob, this.attachments[index].fileName)
            }
        },
        openIncidentFormModal(params) {
            this.$refs.incidentFormModal.show(null, this.entityInformation, params?.node && params?.node.data.incidentId)
        },
        async onIncidentAdded(entityInformation) {
            await this.loadIncidents(entityInformation)
            await this.$refs.patientTree?.reloadTree()
            this.$emit('incident-added', entityInformation)
        },
        async onIncidentClosed(entityInformation) {
            await this.loadIncidents(entityInformation)
            this.$emit('incident-closed', entityInformation)
        },
        async loadIncidents(entityInformation) {
            await IncidentSeverityServices.getSeverities(this.labId)
            this.laboratorySelectedName = StoreUtils.selectedLaboratoryConfiguration?.laboratory.labName
            this.entityInformation = entityInformation
            this.rowData = []
            this.columnDefs = commonIncidentModalGridColumnDefs(params => this.showAttachments(params))
            let error, response
            switch (this.entityInformation.type) {
            case COREENTITY_VALUES.GENERAL.code:
                [error, response] = await this.toCall(IncidentServices.getGeneralIncidents(this.labId))
                break
            case COREENTITY_VALUES.PATIENT.code:
                [error, response] = await this.toCall(IncidentServices.getPatientIncidents(this.labId, this.entityInformation.patId))
                break
            case COREENTITY_VALUES.REQUEST.code:
                [error, response] = await this.toCall(IncidentServices.getRequestIncidents(this.labId, this.entityInformation.requestId))
                break
            case COREENTITY_VALUES.CASE.code:
                [error, response] = await this.toCall(IncidentServices.getCaseIncidents(this.labId, this.entityInformation.casId))
                break
            case COREENTITY_VALUES.SAMPLE.code:
                [error, response] = await this.toCall(IncidentServices.getSampleIncidents(this.labId, this.entityInformation.casId, this.entityInformation.samId))
                break
            case COREENTITY_VALUES.PORTION.code:
                [error, response] = await this.toCall(IncidentServices.getPortionIncidents(this.labId, this.entityInformation.casId, this.entityInformation.samId, this.entityInformation.porId))
                break
            case COREENTITY_VALUES.SUBPORTION.code:
                [error, response] = await this.toCall(IncidentServices.getSubportionsIncidents(this.labId, this.entityInformation.casId, this.entityInformation.samId, this.entityInformation.porId, this.entityInformation.supId))
                break
            }
            if (error || !response) {
                this.showErrorModalGlobal()
                return
            }
            this.rowData = response
            this.entityInformation.incidentsCount = response.length
        },
        async show(defaultOption, patientId) {
            this.columnDefs = commonIncidentModalGridColumnDefs(params => this.showAttachments(params))

            if (this.isGeneral) {
                const entityInformation = {
                    type: COREENTITY_VALUES.GENERAL.code
                }
                await this.loadIncidents(entityInformation)
                this.$refs.bModalA.show()
            } else {
                this.$refs.bModalA.show()
                await this.$refs.patientTree.loadTree(patientId, defaultOption, this.isRequest, this.isGeneral)
            }
        },
        hide() {
            this.columnDefs = null
            this.$refs.bModalA.hide()
        },
        showing() {
            this.showAttach = !this.showAttach
        },
        animating() {
            this.animate = !this.animate
        },
        onGridReady(event) {
            this.gridApi = event.api
            this.columnApi = event.columnApi
        },
        showAttachments(params) {
            this.attachments = params?.node.data.attachments
            this.indentIdAttachOpen = params?.node.data.incidentId
            this.animating()
            this.showing()
        },
        getContextMenuItems(params) {
            if (params?.node) {
                const isMultipleSelection = this.selectedRows.length > 1
                return [
                    {
                        name: 'Ver/Cerrar incidencia',
                        action: () => this.openIncidentFormModal(params),
                        cssClasses: isMultipleSelection ? ['not-show'] : ''
                    },
                    {
                        name: this.$t('component.attachModal.viewAttach'),
                        action: () => this.showAttachments(params),
                        cssClasses: !params.node.data.attachments.length && isMultipleSelection ? ['not-show'] : ''
                    }
                ]
            }
            return []
        },
        exportReport(exportType) {
            const columns = this.$_getColumnsToExport(this.gridApi, this.columnApi)
            if (columns && this.gridApi) {
                let selectedIds = []
                if (this.selectedRows.length) {
                    selectedIds = this.selectedRows.map(incident => incident.incidentId)
                } else {
                    this.gridApi.forEachNodeAfterFilter(node => selectedIds.push(node.data.incidentId))
                }

                return IncidentServices.exportSelectedIncidentsTo(
                    this.$tc('global.statusOptions.incidence', 2),
                    {},
                    columns,
                    selectedIds,
                    exportType)
            }
            return null
        }
    }
}

</script>

<style lang="scss">

  .incidentsModal .incidents-grid {
    height: calc(100vh - 190px);
    min-height: 400px;
  }

  .incidentsModal .modal-body {
    overflow: hidden;
    padding: 0;
    border-radius: .3rem;
  }

  .incidentsModal .modal-body {
    overflow: hidden;
  }

  .incidentsModal .patientCard .flex-btn {
    width: 4% !important;
  }

  .incidentsModal .patientCard .flex-btn {
    width: 4% !important;
  }

  .incidentsModal .patientCard .title-container {
    margin-right: 2rem;
  }

  .incidentsModal .patientCard i {
    font-size: 1.9rem !important;
  }

  .incidentsModal .tree .element .btn.attach-pressed {
    border: 1px solid vt-color(primary);
  }

</style>

<style lang="scss" scoped>

  .incidentsModal .tree {
    border-right: 1px solid #dee2e6;
  }

  .incidentsModal .attach-header {
    border-bottom: 1px solid #dee2e6;
  }

  .incidentsModal .attach-content {
    height: calc(100vh - 185px);
      min-height: 400px;
      overflow: hidden;
    overflow-y: auto;
    margin-right: 5px;
  }

  .incidentsModal .tree-content {
    height: calc(100vh - 200px);
      min-height: 400px;
      overflow: hidden;
    overflow-y: auto;
    margin-right: 0;
  }

  .incidentsModal .tree .element .btn {
    background: transparent;
    color: grey;
    border-color: #dee2e6;
    border-radius: 2px;
    width: 100%;
    margin-bottom: 1%;
  }

  .incidentsModal .tree .element .arrow {
    position: absolute;
    top: 12px;
    right: 12px;
    font-size: .9rem;
  }

  .incidentsModal .tree .element .btn:hover {
    background: vt-color(light-primary);
  }

  .attach-arrow {
    padding: 0 3px;
  }

  .attach-display {
    display: none;
  }

  .attach-pressed {
    background: vt-color(primary) !important;
    color: #FFF !important;
  }

  .incidentsModal .sidepanel {
    border-left: 1px solid #dee2e6;
    background: #FFF;
    width: 300px;
    position: absolute;
    right: -300px;
    height: 100%;
    top: 0;
    transition: .3s;
    box-shadow: 0 3px 5px 0 #9c9c9c;
  }

  .incidentsModal .sidepanel.animation {
    right: 0;
  }

  .incidentsModal .sidepanel .header {
    border-bottom: 1px solid #dee2e6;
  }

  .incidentsModal .sidepanel .title {
    color: vt-color(primary);
  }

  .incidentsModal .sidepanel .content {
    height: 100%;
    overflow: hidden;
    overflow-y: scroll;
  }

  .incidentsModal .sidepanel .file {
    border-bottom: 1px solid #dee2e6;
  }

  .incidentsModal .sidepanel .file .others {
    color: grey;
    font-size: .9rem;
  }

  .incidentsModal .sidepanel .element .col-md-4 {
    max-width: 100% !important;
    padding-left: 10px;
    margin-top: 5px;
    margin-bottom: 0 !important;
  }

</style>
